import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import CategoryPicker from '../common/CategoryPicker';
import { FOOD_CATEGORIES, AVOID_CATEGORIES, CLASSIFICATION_CATEGORIES, CATEGORY_ICONS } from '../../services/constants';
import './Profile.css';

export default function SettingsPage() {
  const { user, updateUser, logout } = useAuth();
  const fileRef = useRef();
  const [username, setUsername] = useState(user?.username || '');
  const [about, setAbout] = useState(user?.about || '');
  const [birthday, setBirthday] = useState(user?.birthday ? new Date(user.birthday).toISOString().split('T')[0] : '');
  const [emailNotifications, setEmailNotifications] = useState(user?.emailNotifications ?? true);
  const [hidden, setHidden] = useState(user?.hidden ?? false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState('');
  const [prefCategories, setPrefCategories] = useState(user?.preferences?.categories || []);
  const [prefAvoids, setPrefAvoids] = useState(user?.preferences?.avoids || []);
  const [prefClassifications, setPrefClassifications] = useState(user?.preferences?.classifications || []);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMessage, setPrefMessage] = useState('');

  const togglePref = (list, setList, item) => {
    setList(list.includes(item) ? list.filter(i => i !== item) : [...list, item]);
  };

  const handleSavePreferences = async () => {
    setPrefSaving(true);
    setPrefMessage('');
    try {
      await authAPI.updatePreferences({ categories: prefCategories, avoids: prefAvoids, classifications: prefClassifications });
      updateUser({ preferences: { categories: prefCategories, avoids: prefAvoids, classifications: prefClassifications } });
      setPrefMessage('Preferences saved!');
    } catch (err) {
      setPrefMessage(err.message);
    }
    setPrefSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const data = await authAPI.updateProfile({
        username,
        about,
        birthday: birthday || null,
        emailNotifications,
        hidden,
      });
      updateUser(data.user);
      setMessage('Settings saved!');
    } catch (err) {
      setMessage(err.message);
    }
    setSaving(false);
  };

  const handleAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const data = await authAPI.uploadAvatar(formData);
      updateUser(data.user);
    } catch { /* ignore */ }
  };

  const handleChangePassword = async () => {
    setPwMessage('');
    if (!currentPassword) { setPwMessage('Current password is required'); return; }
    if (newPassword.length < 6) { setPwMessage('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmNewPassword) { setPwMessage('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      const data = await authAPI.changePassword(currentPassword, newPassword);
      setPwMessage(data.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setPwMessage(err.message);
    }
    setPwSaving(false);
  };

  return (
    <div className="settings-page fade-in">
      <div className="settings-card card">
        <h1>⚙️ Settings</h1>

        <div className="settings-section">
          <h3>Profile Picture</h3>
          <div className="avatar-upload">
            <div className="avatar-upload-img">
              {user?.profilePicture ? <img src={user.profilePicture} alt="avatar" /> : user?.username?.[0]?.toUpperCase()}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current.click()}>Change Photo</button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleAvatar} />
          </div>
        </div>

        <div className="settings-section">
          <h3>Account</h3>
          <div className="form-group">
            <label>Username</label>
            <input className="input-field" value={username} onChange={e => setUsername(e.target.value)} />
          </div>
          <div className="form-group mt-2">
            <label>Email</label>
            <input className="input-field" value={user?.email || ''} disabled />
          </div>
          <div className="form-group mt-2">
            <label>Birthday</label>
            <input type="date" className="input-field" value={birthday} onChange={e => setBirthday(e.target.value)} />
          </div>
          <div className="form-group mt-2">
            <label>About ({about.length}/124)</label>
            <textarea className="input-field" value={about} onChange={e => setAbout(e.target.value.slice(0, 124))} rows={3} maxLength={124} />
          </div>
        </div>

        <div className="settings-section">
          <h3>Privacy & Notifications</h3>
          <div className="form-group">
            <label className="toggle-label">
              <span>Email Notifications</span>
              <input type="checkbox" checked={emailNotifications} onChange={e => setEmailNotifications(e.target.checked)} />
              <span className="toggle-switch" />
            </label>
          </div>
          <div className="form-group mt-2">
            <label className="toggle-label">
              <span>Hide Profile</span>
              <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
              <span className="toggle-switch" />
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>Food Preferences</h3>
          <CategoryPicker
            label="Food Categories"
            items={FOOD_CATEGORIES}
            selected={prefCategories}
            onToggle={(item) => togglePref(prefCategories, setPrefCategories, item)}
            icons={CATEGORY_ICONS}
          />
          <CategoryPicker
            label="Free From"
            items={AVOID_CATEGORIES}
            selected={prefAvoids}
            onToggle={(item) => togglePref(prefAvoids, setPrefAvoids, item)}
          />
          <CategoryPicker
            label="Dietary Lifestyle"
            items={CLASSIFICATION_CATEGORIES}
            selected={prefClassifications}
            onToggle={(item) => togglePref(prefClassifications, setPrefClassifications, item)}
          />
          {prefMessage && (
            <div className="auth-error" style={{
              background: prefMessage.includes('saved') ? '#DCFCE7' : undefined,
              color: prefMessage.includes('saved') ? '#15803D' : undefined,
              marginTop: 10
            }}>{prefMessage}</div>
          )}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-primary" onClick={handleSavePreferences} disabled={prefSaving}>
              {prefSaving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h3>Change Password</h3>
          <div className="form-group">
            <label>Current Password</label>
            <input type="password" className="input-field" placeholder="Enter current password"
              value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <div className="form-group mt-2">
            <label>New Password</label>
            <input type="password" className="input-field" placeholder="At least 6 characters"
              value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" />
          </div>
          <div className="form-group mt-2">
            <label>Confirm New Password</label>
            <input type="password" className="input-field" placeholder="Repeat new password"
              value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} autoComplete="new-password" />
          </div>
          {pwMessage && (
            <div className="auth-error" style={{
              background: pwMessage.includes('successfully') ? '#DCFCE7' : undefined,
              color: pwMessage.includes('successfully') ? '#15803D' : undefined,
              marginTop: 10
            }}>{pwMessage}</div>
          )}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={handleChangePassword} disabled={pwSaving}>
              {pwSaving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>

        {message && <div className={`auth-error`} style={{ background: message.includes('saved') ? '#DCFCE7' : undefined, color: message.includes('saved') ? '#15803D' : undefined, marginBottom: 16 }}>{message}</div>}

        <div className="step-actions">
          <button className="btn btn-danger" onClick={logout}>Logout</button>
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {user?.role === 'admin' && (
          <div style={{ marginTop: 20, padding: '12px 16px', background: '#FFF7ED', borderRadius: 10, fontSize: 13 }}>
            <strong>🔒 Role:</strong> Admin
          </div>
        )}
      </div>
    </div>
  );
}
